import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
} from "react";
import type { Language, TranslationNamespace } from "./types";

import koCommon from "./ko/common.json";
import koMain from "./ko/main.json";
import koAbout from "./ko/about.json";
import koProgram from "./ko/program.json";
import koContact from "./ko/contact.json";
import koPortfolio from "./ko/portfolio.json";
import koInsight from "./ko/insight.json";
import koTeam from "./ko/team.json";
import koIr from "./ko/ir.json";
import koPolicy from "./ko/policy.json";
import koModal from "./ko/modal.json";

import enCommon from "./en/common.json";
import enMain from "./en/main.json";
import enAbout from "./en/about.json";
import enProgram from "./en/program.json";
import enContact from "./en/contact.json";
import enPortfolio from "./en/portfolio.json";
import enInsight from "./en/insight.json";
import enTeam from "./en/team.json";
import enIr from "./en/ir.json";
import enPolicy from "./en/policy.json";
import enModal from "./en/modal.json";

const STORAGE_KEY = "bpp-language";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TranslationData = Record<string, any>;

const translations: Record<
  Language,
  Record<TranslationNamespace, TranslationData>
> = {
  ko: {
    common: koCommon,
    main: koMain,
    about: koAbout,
    program: koProgram,
    contact: koContact,
    portfolio: koPortfolio,
    insight: koInsight,
    team: koTeam,
    ir: koIr,
    policy: koPolicy,
    modal: koModal,
  },
  en: {
    common: enCommon,
    main: enMain,
    about: enAbout,
    program: enProgram,
    contact: enContact,
    portfolio: enPortfolio,
    insight: enInsight,
    team: enTeam,
    ir: enIr,
    policy: enPolicy,
    modal: enModal,
  },
};

function getNestedValue(obj: unknown, path: string): string {
  const keys = path.split(".");
  let current: unknown = obj;
  for (const key of keys) {
    if (
      current === null ||
      current === undefined ||
      typeof current !== "object"
    ) {
      return path;
    }
    current = (current as Record<string, unknown>)[key];
  }
  return typeof current === "string" ? current : path;
}

interface LanguageContextType {
  language: Language;
  toggleLanguage: () => void;
  t: (namespace: TranslationNamespace, key: string) => string;
}

const LanguageContext = createContext<LanguageContextType | undefined>(
  undefined
);

function getInitialLanguage(): Language {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === "ko" || stored === "en") return stored;
  } catch {
    // SSR or localStorage unavailable
  }
  return "ko";
}

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [language, setLanguage] = useState<Language>(getInitialLanguage);

  const toggleLanguage = useCallback(() => {
    setLanguage((prev) => {
      const next = prev === "ko" ? "en" : "ko";
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {
        // ignore
      }
      return next;
    });
  }, []);

  const t = useCallback(
    (namespace: TranslationNamespace, key: string): string => {
      const ns = translations[language][namespace];
      if (!ns) return key;
      return getNestedValue(ns, key);
    },
    [language]
  );

  const value = useMemo(
    () => ({ language, toggleLanguage, t }),
    [language, toggleLanguage, t]
  );

  return (
    <LanguageContext.Provider value={value}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = (): LanguageContextType => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within a LanguageProvider");
  }
  return context;
};
